import {GridMeasureRequest} from '@mintdata-wsp/common/state/ui/visualDesigner/grid/GridMeasure';
import {
    CellEvent,
    CellRenderContext,
    CellSize,
    ColumnIndex,
    IGridModelView,
    IMeasureBag,
    IMutableGridModel,
    RowIndex
} from '@mintdata-wsp/common/state/ui/visualDesigner/grid/GridModel';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('./GridMeasureView.less');
const cx = classnames.bind(styles);

export class MeasureBag implements IMeasureBag
{
    private readonly _cellSizes:CellSize[][];

    constructor(private readonly _numRows:number,
                private readonly _numColumns:number)
    {
        this._cellSizes = new Array(this._numRows);
    }

    @bind
    getNumColumns():number
    {
        return this._numColumns;
    }

    @bind
    getNumRows():number
    {
        return this._numRows;
    }

    @bind
    getCellSize(rowIndex:RowIndex, columnIndex:ColumnIndex):CellSize
    {
        return this._cellSizes[rowIndex][columnIndex];
    }

    @bind
    getWidestColumn(columnIndex:ColumnIndex):number
    {
        let maxWidth = 0;

        for( let rowIndex = 0; rowIndex < this._cellSizes.length; rowIndex++ )
        {
            const cellSize = this.getCellSize(rowIndex, columnIndex);
            const cellWidth = cellSize.width;

            if( cellWidth > maxWidth )
                maxWidth = cellWidth;
        }

        return maxWidth;
    }

    @bind
    setCellSize(rowIndex:RowIndex, columnIndex:ColumnIndex, cellSize:CellSize):void
    {
        let rowSizes = this._cellSizes[rowIndex];

        if( !rowSizes )
        {
            rowSizes = new Array(this._numColumns);
            this._cellSizes[rowIndex] = rowSizes;
        }

        rowSizes[columnIndex] = cellSize;
    }

    @bind
    print():void
    {
        const tableFn = console.table;
        if( !tableFn || !(typeof tableFn === 'function') )
            return;

        const numRows = this._numRows;
        const numColumns = this._numColumns;

        const tableData = [];
        for( let rowIndex = 0; rowIndex < numRows; rowIndex++ )
        {
            const row = {};
            tableData.push(row);

            for( let columnIndex = 0; columnIndex < numColumns; columnIndex++ )
                row[columnIndex] = this.getCellSize(rowIndex, columnIndex).toString();
        }

        tableFn(tableData);
    }
}

export interface GridMeasureViewProps extends GridMeasureRequest
{
    isVisible:boolean;
    className?:string;
}

@observer
export class GridMeasureView extends React.Component<GridMeasureViewProps, any>
{
    componentDidMount():void
    {
    }

    @bind
    private _onRef(ref:HTMLElement)
    {
        if( !ref )
            return;

        setTimeout(() =>
        {
            this._doMeasure(ref);
        }, 0);
    }

    @bind
    private _doMeasure(ref:HTMLElement)
    {
        const {
            numRowsToMeasure, numColumnsToMeasure,
            horizontalCellPadding, verticalCellPadding
        } = this.props;
        const bag = new MeasureBag(numRowsToMeasure, numColumnsToMeasure);

        const measureRowHolders = ref.children;
        for( let i = 0; i < measureRowHolders.length; i++ )
        {
            const measureRowContainer = measureRowHolders.item(i);
            const cellsInRow = measureRowContainer.children;
            const numCellsInRow = cellsInRow.length;

            for( let j = 0; j < numCellsInRow; j++ )
            {
                const cellDomElement:Element = cellsInRow.item(j);
                const cellBounds = cellDomElement.getBoundingClientRect();

                const width = Math.ceil(cellBounds.width) + (horizontalCellPadding * 2);
                const height = Math.ceil(cellBounds.height) + (verticalCellPadding * 2);

                bag.setCellSize(i, j, new CellSize(width, height));
            }
        }

        this.props.onMeasureDone(bag);
    }

    @bind
    private _renderRow(gridModel:IMutableGridModel,
                       gridView:IGridModelView,
                       rowIndex:RowIndex):React.ReactElement<any>[]
    {
        const elements:React.ReactElement<any>[] = [];
        const numCols = this.props.numColumnsToMeasure;

        for( let columnIndex = 0; columnIndex < numCols; columnIndex++ )
        {
            const cell = gridView.getCellAt(rowIndex, columnIndex);
            const renderer = this.props.gridModel.getAppearance().getRendererFor(cell);

            const cellStyle = {};

            const cellArea = gridView.getAreaAt(rowIndex, columnIndex);
            const isRowSelected = gridView.isRowSelected(rowIndex);
            const cellAreaClassNames = gridModel.getAppearance()
                .getCellAreaStyles()
                .getCellClassNames(cellArea, isRowSelected);
            const ref = null;
            const noOpHandler = (e:CellEvent) =>
            {
            };
            const ctx = new CellRenderContext(true, cell, `rc-${rowIndex}-col${columnIndex}`, cellStyle, noOpHandler, cellAreaClassNames, ref);
            const cellElement = renderer.render(ctx);

            elements.push(cellElement);
        }

        return elements;
    }

    @bind
    _renderRows()
    {
        const {gridModel, viewToMeasure, numRowsToMeasure, className} = this.props;
        const elements:React.ReactElement<any>[] = [];
        const numRows = numRowsToMeasure;

        for( let rowIndex = 0; rowIndex < numRows; rowIndex++ )
        {
            const rowHolder = React.createElement('div',
                {
                    key: `row-holder-${rowIndex}`,
                    className: cx('measure-row-container', className)
                },
                this._renderRow(gridModel, viewToMeasure, rowIndex));
            elements.push(rowHolder);
        }

        return elements;
    }

    render()
    {
        const {className} = this.props;
        const style = this.props.isVisible ? {visibility: 'visible'} : {visibility: 'hidden'};

        return <div style={style} ref={this._onRef} className={className}>
            {this._renderRows()}
        </div>;
    }
}

